import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash-es';
import { Observable, Subscription, of, combineLatest, ReplaySubject } from 'rxjs';
import { take, map, filter, debounceTime, startWith, switchMap, shareReplay } from 'rxjs/operators';
import { CriteriaGroup } from '../../template/template.component';
import { AcceptanceCriterion, TemplateGroupInfo, DataParametersResponse, BaseDataset } from '../../../../shared/templateGen/templateInterfaces';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import { ToastrService } from 'ngx-toastr';
import { ColumnResizeService, ColumnWidthState, ESSENTIAL_COLUMNS } from './column-resize.service';
import { TestWithMetadata } from '../test-id.service';
import {
  MacroScenarioMicroSpecState,
  MicroScenarioSpecificationModel
} from '../../template/three-column-workspace/three-column-workspace.component';


interface TestItem {
  ID: string;
  batchIndex: number;
  [key: string]: any;
}

export interface MicroScenarioUIData {
  id: string;
  specification: string;
  tags: string[];
  macroId?: string;
  gherkinSection?: 'given' | 'when' | 'then';
}

interface MicroScenarioTestGroup {
  scenario: MicroScenarioUIData;
  tests: TestWithMetadata[];
}

// Interface for the modal state
interface ParameterModalState {
  paramId: string; // e.g., CTX_STATO_INIZIALE
  currentValue: string; // e.g., "Evento M.Rossi"
  definition: BaseDataset | null; // From DataParameters
  usageCount: number; // How many tests use this specific param
  distinctValues: string[]; // All values used for this param across suite
  testsAffected: TestWithMetadata[]; // The tests containing this param
}

@Component({
  selector: 'app-test-table',
  templateUrl: './test-table.component.html',
  styleUrls: ['./test-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestTableComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('compactTable') compactTable: ElementRef;
  @ViewChildren('batchTableView') batchTables: QueryList<ElementRef>;
  @Input() showActions: boolean = true;

  // MODIFIED: Add Outputs to notify the parent component of changes
  @Output() testUpdated = new EventEmitter<TestWithMetadata>();
  @Output() testDeleted = new EventEmitter<TestWithMetadata>();
  @Input() tests: TestWithMetadata[] = [];
  @Input() columns: string[] = [];
  @Input() showMicroScenarioColumn: boolean = true;
  @Input() isSimplifiedDisplayMode: boolean = false;

  displayableTests$: Observable<TestWithMetadata[]>;
  private testsInput$ = new ReplaySubject<TestWithMetadata[]>(1);

  readonly criteriaGroups$: Observable<CriteriaGroup[]> = this.statoSessioneService.criteriaGroups$;
  groupedByMicroScenarioTests$: Observable<MicroScenarioTestGroup[]>;

  protected allSubTablesExpanded = false;
  isCompactView = false;
  selectedTest: TestWithMetadata | null = null;

  // New Variables for Parameter Modal
  selectedParamState: ParameterModalState | null = null;
  dataParameters: DataParametersResponse | null = null;

  // Edit Mode Variables
  editValue: string = '';
  editScope: 'single' | 'all_same_value' | 'all_occurrences' = 'single';

  private subscription = new Subscription();
  private memoizedSubTableColumns = new Map<string, string[]>();
  private memoizedSubTableRows = new Map<string, any[]>();
  private hasSubTablesCache = new Map<string, boolean>();

  constructor(
    private modalService: NgbModal,
    private statoSessioneService: StatoSessioneService,
    public columnResizeService: ColumnResizeService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService
  ) {
    this.testsInput$.next([]);

    // Subscribe to Data Parameters to have definitions ready
    this.subscription.add(
      this.statoSessioneService.dataParameters$.subscribe(params => {
        this.dataParameters = params;
      })
    );

    this.displayableTests$ = this.testsInput$.pipe(
      switchMap(inputTests => {
        if (this.isSimplifiedDisplayMode && inputTests) {
          return of(inputTests);
        }
        return this.statoSessioneService.generatedTests$.pipe(startWith([]));
      }),
      shareReplay(1)
    );

    this.subscription.add(
      this.displayableTests$.pipe(
        filter(tests => !!tests && tests.length > 0),
        take(1),
        debounceTime(0)
      ).subscribe(() => {
        this.resetColumns();
        this.cdr.markForCheck();
      })
    );

    this.groupedByMicroScenarioTests$ = combineLatest([
      this.displayableTests$,
      this.statoSessioneService.microScenarioSpecifications$.pipe(startWith(new Map<string, MacroScenarioMicroSpecState>()))
    ]).pipe(
      map(([tests, microSpecsRootMap]) => {
        if (this.isSimplifiedDisplayMode) {
          return [];
        }

        if (!tests || tests.length === 0) {
          return [];
        }

        const microIdToSpecModel = new Map<string, MicroScenarioSpecificationModel>();
        const gherkinSections: Array<'given' | 'when' | 'then'> = ['given', 'when', 'then'];

        if (microSpecsRootMap) {
          for (const macroState of microSpecsRootMap.values()) {
            for (const sectionKey of gherkinSections) {
              const sectionState = macroState[sectionKey];
              if (sectionState && sectionState.specifications) {
                for (const spec of sectionState.specifications) {
                  microIdToSpecModel.set(spec.id, spec);
                }
              }
            }
          }
        }

        const groupsByMicroId = new Map<string, { scenarioData?: MicroScenarioUIData, tests: TestWithMetadata[] }>();

        for (const test of tests) {
          const microId = test.metadata.microScenarioId;
          if (!microId) {
            console.warn('Test found without microScenarioId:', test.metadata.displayId);
            continue;
          }

          if (!groupsByMicroId.has(microId)) {
            const specModel = microIdToSpecModel.get(microId);
            let scenarioUIData: MicroScenarioUIData;
            if (specModel) {
              scenarioUIData = {
                id: specModel.id,
                specification: specModel.specification,
                tags: specModel.tags || [],
                macroId: specModel.macro_id,
                gherkinSection: specModel.gherkin_section
              };
            } else {
              console.warn(`MicroScenarioSpecificationModel not found for microId: ${microId}. Used by Test ID: ${test.metadata.displayId}`);
              scenarioUIData = {
                id: microId,
                specification: `Unknown Micro Scenario (ID: ${microId})`,
                tags: []
              };
            }
            groupsByMicroId.set(microId, { scenarioData: scenarioUIData, tests: [] });
          }
          groupsByMicroId.get(microId)!.tests.push(test);
        }

        const result: MicroScenarioTestGroup[] = [];
        for (const [microId, groupData] of groupsByMicroId.entries()) {
          if (groupData.scenarioData) {
            result.push({
              scenario: groupData.scenarioData,
              tests: groupData.tests
            });
          } else {
            console.error(`Scenario data was unexpectedly missing for microId ${microId}. Creating a fallback group.`);
            result.push({
              scenario: { id: microId, specification: `Error: Data unavailable for ${microId}`, tags: [] },
              tests: groupData.tests
            });
          }
        }
        if (!this.isSimplifiedDisplayMode) {
          result.sort((a, b) => a.scenario.id.localeCompare(b.scenario.id));
        }
        return result;
      })
    );
  }

  ngOnInit() {
    if (this.isSimplifiedDisplayMode) {
      this.isCompactView = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tests) {
      this.testsInput$.next(this.tests || []);
      if (this.isSimplifiedDisplayMode && this.tests && this.tests.length > 0) {
        this.resetColumns();
      } else if (this.isSimplifiedDisplayMode && (!this.tests || this.tests.length === 0)) {
        // this.columnResizeService.clearColumns();
      }
    }
    if (changes.isSimplifiedDisplayMode && this.isSimplifiedDisplayMode) {
      this.isCompactView = true;
      this.testsInput$.next(this.tests || []);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.memoizedSubTableColumns.clear();
    this.memoizedSubTableRows.clear();
    this.hasSubTablesCache.clear();
    this.columnResizeService.destroy();
  }

  toggleViewMode(): void {
    this.isCompactView = !this.isCompactView;
    setTimeout(() => this.resetColumns(), 0);
  }

  /**
   * MODIFIED: Emits an event to the parent instead of calling the service directly.
   */
  deleteTest(test: TestWithMetadata): void {
    if (confirm('Are you sure you want to delete this test?')) {
      if (!test?.metadata?.displayId) {
        console.error('Cannot delete test without a valid displayId', test);
        return;
      }
      // Instead of calling the service, emit the event for the parent to handle.
      this.testDeleted.emit(test);
    }
  }

  toggleAllSubTables(): void {
    this.allSubTablesExpanded = !this.allSubTablesExpanded;

    setTimeout(() => {
      if (this.allSubTablesExpanded) {
        this.recalculateColumnsForSubtables();
      } else {
        this.resetColumns();
      }
      this.cdr.markForCheck();
    }, 150);
  }

  private recalculateColumnsForSubtables(): void {
    this.displayableTests$.pipe(take(1)).subscribe(currentTests => {
      if (!currentTests || currentTests.length === 0) return;

      const dataColumns = Object.keys(currentTests[0].data).filter(col => col !== 'metadata');
      const essentialColumns = dataColumns.filter(col =>
        this.columnResizeService.isEssentialColumn(col)
      );
      const nonEssentialColumns = dataColumns.filter(col =>
        !this.columnResizeService.isEssentialColumn(col)
      );

      const subtableColumns = new Set<string>();
      currentTests.forEach(test => {
        nonEssentialColumns.forEach(column => {
          if (this.isSubTable(test.data[column])) {
            subtableColumns.add(column);
          }
        });
      });

      let currentState: ColumnWidthState = {};
      this.columnResizeService.columnState$.pipe(take(1)).subscribe(state => {
        currentState = state;
      });

      const columnConfig: { [key: string]: number } = {};
      essentialColumns.forEach(col => {
        if (currentState[col]) {
          columnConfig[col] = currentState[col].currentWidth;
        } else {
          if (col === ESSENTIAL_COLUMNS.ID) {
            columnConfig[col] = 4;
          } else if (col === ESSENTIAL_COLUMNS.TYPE) {
            columnConfig[col] = 9;
          }
        }
      });

      // MODIFICATION: Conditionally add Actions column width
      let actionsWidth = 0;
      if (this.showActions) {
        actionsWidth = 11;
        columnConfig[ESSENTIAL_COLUMNS.ACTIONS] = actionsWidth;
      }

      const essentialColumnsWidth = essentialColumns.reduce((total, col) => {
        return total + (columnConfig[col] || 0);
      }, 0) + actionsWidth;
      const remainingWidth = 100 - essentialColumnsWidth;

      const subtableColumnsArray = Array.from(subtableColumns);
      const regularColumns = nonEssentialColumns.filter(col => !subtableColumns.has(col));
      const onlySubtableColumns = subtableColumnsArray.length > 0 && regularColumns.length === 0;

      if (onlySubtableColumns) {
        if (subtableColumnsArray.length === 1) {
          const subtableColumn = subtableColumnsArray[0];
          columnConfig[subtableColumn] = remainingWidth;
        } else {
          const widthPerColumn = remainingWidth / subtableColumnsArray.length;
          subtableColumnsArray.forEach(col => {
            columnConfig[col] = widthPerColumn;
          });
        }
      }
      else if (subtableColumnsArray.length > 0 && regularColumns.length > 0) {
        const minRegularColumnWidth = 10;
        const totalMinWidthForRegular = regularColumns.length * minRegularColumnWidth;
        const spaceForSubtables = Math.max(remainingWidth - totalMinWidthForRegular, 0);

        if (spaceForSubtables > 0) {
          const maxSubtableWidth = 25;
          const widthPerSubtable = Math.min(
            spaceForSubtables / subtableColumnsArray.length,
            maxSubtableWidth
          );
          subtableColumnsArray.forEach(col => {
            columnConfig[col] = widthPerSubtable;
          });
          const totalSubtableWidth = widthPerSubtable * subtableColumnsArray.length;
          const remainingForRegular = remainingWidth - totalSubtableWidth;
          if (remainingForRegular > 0 && regularColumns.length > 0) {
            const widthPerRegular = remainingForRegular / regularColumns.length;
            regularColumns.forEach(col => {
              columnConfig[col] = widthPerRegular;
            });
          }
        } else {
          regularColumns.forEach(col => {
            columnConfig[col] = minRegularColumnWidth;
          });
          const remainingForSubtables = Math.max(remainingWidth - totalMinWidthForRegular, 0);
          if (remainingForSubtables > 0 && subtableColumnsArray.length > 0) {
            const widthPerSubtable = remainingForSubtables / subtableColumnsArray.length;
            subtableColumnsArray.forEach(col => {
              columnConfig[col] = widthPerSubtable;
            });
          } else {
            const equalWidth = remainingWidth / nonEssentialColumns.length;
            nonEssentialColumns.forEach(col => {
              columnConfig[col] = equalWidth;
            });
          }
        }
      } else {
        if (nonEssentialColumns.length > 0) {
          const widthPerColumn = remainingWidth / nonEssentialColumns.length;
          nonEssentialColumns.forEach(col => {
            columnConfig[col] = widthPerColumn;
          });
        }
      }

      // MODIFICATION: Conditionally create the list of columns to initialize
      const columnsToInitialize = [...dataColumns];
      if (this.showActions) {
        columnsToInitialize.push(ESSENTIAL_COLUMNS.ACTIONS);
      }
      this.columnResizeService.initializeColumnsWithCustomWidths(columnsToInitialize, columnConfig);
    });
  }

  hasSubTables(tests: TestWithMetadata[]): boolean {
    const cacheKey = tests && tests.length > 0 ? tests.map(t => t.metadata.displayId).join('-') : 'empty';
    if (this.hasSubTablesCache.has(cacheKey)) {
      return this.hasSubTablesCache.get(cacheKey)!;
    }
    if (!tests || tests.length === 0) {
      this.hasSubTablesCache.set(cacheKey, false);
      return false;
    }
    const hasSubTable = tests.some(test => {
      if (!test.data) { return false; }
      return Object.values(test.data).some(value => this.isSubTable(value));
    });
    this.hasSubTablesCache.set(cacheKey, hasSubTable);
    return hasSubTable;
  }

  openEditModal(test: TestWithMetadata, modal: any): void {
    this.selectedTest = cloneDeep(test);

    // Capture the modal reference
    const modalRef = this.modalService.open(modal, {
      ariaLabelledBy: 'testModalLabel',
      size: 'xl'
    });

    // FIX: Reset columns shortly after the modal opens.
    // The setTimeout allows the browser to apply the `modal-open` class
    // and remove the scrollbar before we recalculate.
    setTimeout(() => this.resetColumns(), 50);

    // FIX: Reset columns again when the modal is closed (either saved or dismissed).
    // This handles the layout shift when the scrollbar reappears.
    modalRef.result.then(
      () => {
        // Closed via save/success
        setTimeout(() => this.resetColumns(), 50);
      },
      () => {
        // Dismissed via 'x', backdrop click, or Esc
        setTimeout(() => this.resetColumns(), 50);
      }
    );
  }

  /**
   * MODIFIED: Emits an event to the parent instead of calling the service directly.
   */
  saveEditedTest(): void {
    if (!this.selectedTest) { return; }

    // Emit the updated test data for the parent component to handle.
    this.testUpdated.emit(cloneDeep(this.selectedTest));

    // Clean up the local modal state. The parent will handle the data update.
    this.selectedTest = null;
    this.modalService.dismissAll();
  }

  get displayColumns(): string[] {
    if (this.columns && this.columns.length > 0) {
      return this.columns.filter(col => col !== 'metadata');
    }
    let derivedColumns: string[] = [];
    this.displayableTests$.pipe(take(1)).subscribe(currentTests => {
      if (currentTests && currentTests.length > 0 && currentTests[0]?.data) {
        derivedColumns = Object.keys(currentTests[0].data).filter(col => col !== 'metadata');
      }
    });
    return derivedColumns;
  }

  getFieldType(value: any, columnName: string): string {
    if (this.isSubTable(value)) { return 'subtable'; }
    if (this.isArray(value) && !this.isSubTable(value)) { return 'array'; }
    if (this.isLongText(value)) { return 'longtext'; }
    return 'simple';
  }
  isSubTable(value: any): boolean {
    const result = value && typeof value === 'object' && ((value.headers && Array.isArray(value.headers) && value.rows && Array.isArray(value.rows)) || (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && !Array.isArray(value[0])));
    return !!result;
  }
  isArray(value: any): boolean { return Array.isArray(value); }
  getSubTableColumns(subTable: any): string[] {
    if (!subTable) return [];
    const cacheKey = JSON.stringify(subTable);
    if (this.memoizedSubTableColumns.size > 100) { this.memoizedSubTableColumns.clear(); }
    if (this.memoizedSubTableColumns.has(cacheKey)) { return this.memoizedSubTableColumns.get(cacheKey)!; }
    let columns: string[] = [];
    if (subTable.headers) { columns = [...subTable.headers]; } else if (Array.isArray(subTable) && subTable.length > 0) {
      const allKeys = new Set<string>();
      subTable.forEach(row => { if (row && typeof row === 'object') { Object.keys(row).forEach(key => allKeys.add(key)); } });
      columns = Array.from(allKeys);
    }
    this.memoizedSubTableColumns.set(cacheKey, columns);
    return columns;
  }
  getSubTableRows(subTable: any): any[] {
    if (!subTable) return [];
    const cacheKey = JSON.stringify(subTable);
    if (this.memoizedSubTableRows.size > 100) { this.memoizedSubTableRows.clear(); }
    if (this.memoizedSubTableRows.has(cacheKey)) { return this.memoizedSubTableRows.get(cacheKey)!; }
    let rows: any[] = [];
    if (subTable.headers && Array.isArray(subTable.rows)) {
      rows = subTable.rows.map((row: any[]) => {
        return subTable.headers.reduce((obj: any, header: string, index: number) => {
          obj[header] = row[index];
          return obj;
        }, {});
      });
    } else if (Array.isArray(subTable)) { rows = subTable; } else if (subTable.rows && Array.isArray(subTable.rows)) { rows = subTable.rows; }
    this.memoizedSubTableRows.set(cacheKey, rows);
    return rows;
  }
  isSimpleInput(value: any): boolean { return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'; }
  isLongText(value: any): boolean { return typeof value === 'string' && value.length > 100; }
  getInputType(value: any): string {
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'checkbox';
    return 'text';
  }
  addArrayItem(column: string): void {
    if (!this.selectedTest) return;
    if (!Array.isArray(this.selectedTest.data[column])) { this.selectedTest.data[column] = []; }
    this.selectedTest.data[column] = [...this.selectedTest.data[column], ''];
    this.cdr.markForCheck();
  }
  removeArrayItem(column: string, index: number): void {
    if (!this.selectedTest) return;
    this.selectedTest.data[column] = [...this.selectedTest.data[column].slice(0, index), ...this.selectedTest.data[column].slice(index + 1)];
    this.cdr.markForCheck();
  }
  addSubTableRow(column: string): void {
    if (!this.selectedTest) return;
    const headers = this.getSubTableColumns(this.selectedTest.data[column]);
    const newRow = Object.fromEntries(headers.map(header => [header, '']));
    if (Array.isArray(this.selectedTest.data[column])) { this.selectedTest.data[column] = [...this.selectedTest.data[column], newRow]; } else if (this.selectedTest.data[column].rows) {
      if (this.selectedTest.data[column].rows.length > 0 && Array.isArray(this.selectedTest.data[column].rows[0])) {
        const newRowArray = headers.map(() => '');
        this.selectedTest.data[column] = { ...this.selectedTest.data[column], rows: [...this.selectedTest.data[column].rows, newRowArray] };
      } else { this.selectedTest.data[column] = { ...this.selectedTest.data[column], rows: [...this.selectedTest.data[column].rows, newRow] }; }
    } else { this.selectedTest.data[column] = [newRow]; }
    this.memoizedSubTableRows.delete(JSON.stringify(this.selectedTest.data[column]));
    this.cdr.markForCheck();
  }
  removeSubTableRow(column: string, index: number): void {
    if (!this.selectedTest) return;
    if (Array.isArray(this.selectedTest.data[column])) { this.selectedTest.data[column] = [...this.selectedTest.data[column].slice(0, index), ...this.selectedTest.data[column].slice(index + 1)]; } else if (this.selectedTest.data[column].rows) { this.selectedTest.data[column] = { ...this.selectedTest.data[column], rows: [...this.selectedTest.data[column].rows.slice(0, index), ...this.selectedTest.data[column].rows.slice(index + 1)] }; }
    this.memoizedSubTableRows.delete(JSON.stringify(this.selectedTest.data[column]));
    this.cdr.markForCheck();
  }
  trackByIndex(index: number): number { return index; }
  trackByFn(index: number, item: any): string | number {
    if (item && item.metadata && item.metadata.displayId) { return item.metadata.displayId; } else if (item && item.ID) { return item.ID; } else if (item && item.id) { return item.id; }
    return index;
  }
  trackByGroupFn(index: number, group: MicroScenarioTestGroup): string { return group.scenario.id; }
  getSubTableRowCount(subTableData: any): number { if (Array.isArray(subTableData)) { return subTableData.length; } return subTableData?.rows?.length || 0; }

  resetColumns(): void {
    setTimeout(() => {
      this.displayableTests$.pipe(take(1)).subscribe(currentTests => {
        if (currentTests && currentTests.length > 0) {
          const dataCols = (this.columns && this.columns.length > 0)
            ? this.columns.filter(c => c !== 'metadata')
            : Object.keys(currentTests[0].data).filter(col => col !== 'metadata');

          const finalDisplayColumns = [...dataCols];

          // MODIFICATION: Conditionally build the list of columns to initialize
          const columnsToInitialize = [...finalDisplayColumns];
          if (this.showActions) {
            columnsToInitialize.push(ESSENTIAL_COLUMNS.ACTIONS);
          }

          const initialWidths: { [key: string]: number } = {};
          if (finalDisplayColumns.includes(ESSENTIAL_COLUMNS.ID)) {
            initialWidths[ESSENTIAL_COLUMNS.ID] = 4;
          }
          if (finalDisplayColumns.includes(ESSENTIAL_COLUMNS.TYPE)) {
            initialWidths[ESSENTIAL_COLUMNS.TYPE] = 9;
          }
          if (this.showActions) {
            initialWidths[ESSENTIAL_COLUMNS.ACTIONS] = 5;
          }

          const essentialWidth = Object.values(initialWidths).reduce((sum, w) => sum + w, 0);
          const remainingWidth = 100 - essentialWidth;

          const nonEssentialColumns = finalDisplayColumns.filter(col =>
            !this.columnResizeService.isEssentialColumn(col)
          );

          if (nonEssentialColumns.length > 0) {
            const widthPerColumn = Math.max(0, remainingWidth / nonEssentialColumns.length);
            nonEssentialColumns.forEach(col => {
              initialWidths[col] = widthPerColumn;
            });
          } else if (remainingWidth < 0) {
            const equallyReduced = 100 / (columnsToInitialize.length);
            columnsToInitialize.forEach(col => initialWidths[col] = equallyReduced);
          }

          this.columnResizeService.initializeColumnsWithCustomWidths(columnsToInitialize, initialWidths);
          this.cdr.markForCheck();
        } else {
          // this.columnResizeService.clearColumns();
          this.cdr.markForCheck();
        }
      });
    }, 0);
  }

  getCellStyle(column: string): object {
    if (column === ESSENTIAL_COLUMNS.TYPE) {
      return {
        fontSize: '0.65rem'
      };
    }
    return {};
  }

  getColumnStyle(column: string): object {
    return this.columnResizeService.getColumnStyle(column);
  }

  startResize(event: MouseEvent, columnIndex: number, tableElement: HTMLElement): void {
    this.columnResizeService.startResize(event, columnIndex, tableElement);
  }

  /**
   * Handles the click on the blue/green data pills.
   */
  handleDataClick(event: MouseEvent, test: TestWithMetadata): void {
    const target = event.target as HTMLElement;
    const dataElement = target.closest('.interactive-data');

    if (dataElement) {
      const fieldId = dataElement.getAttribute('data-field');
      const humanValue = dataElement.textContent?.trim();

      if (fieldId && humanValue) {
        this.openParameterModal(fieldId, humanValue, test);
      }
      event.stopPropagation();
    }
  }

  openParameterModal(paramId: string, currentValue: string, sourceTest: TestWithMetadata) {
    // 1. Find Definition using normalized matching
    const normalizedTarget = this.normalizeParamId(paramId);
    const definition = this.dataParameters?.analysis_results?.test_data_management?.base_dataset?.find(p => {
      const normalizedField = this.normalizeParamId(this.extractParamName(p.field));
      return normalizedField === normalizedTarget;
    }) || null;

    // 2. Calculate Stats across ALL currently displayed tests
    let allTests: TestWithMetadata[] = [];
    this.displayableTests$.pipe(take(1)).subscribe(t => allTests = t);

    const affectedTests: TestWithMetadata[] = [];
    const valuesSet = new Set<string>();

    // Regex to find this specific parameter ID in the HTML
    const paramRegex = new RegExp(`data-field="${paramId}"`, 'i');

    allTests.forEach(t => {
      // Check all data columns (excluding metadata)
      const hasParam = Object.values(t.data).some(val =>
        typeof val === 'string' && paramRegex.test(val)
      );

      if (hasParam) {
        affectedTests.push(t);
        // Extract values used in this test for this param
        this.extractValuesForParam(t, paramId).forEach(v => valuesSet.add(v));
      }
    });

    this.selectedParamState = {
      paramId: paramId,
      currentValue: currentValue,
      definition: definition,
      usageCount: affectedTests.length,
      distinctValues: Array.from(valuesSet),
      testsAffected: affectedTests
    };

    // Initialize Edit State
    this.editValue = currentValue;
    this.editScope = 'single'; // Default to safest option

    // Open Modal
    this.selectedTest = sourceTest; // Temporarily store source test for 'single' edit context
    this.modalService.open(this.parameterModalTemplate, { size: 'lg', centered: true });
  }

  /**
   * Helper to parse "[CTX] PARAM_NAME" -> "PARAM_NAME"
   */
  private extractParamName(rawField: string): string {
    if (!rawField) return '';
    // Handle bracket format: "[CTX] name"
    const bracketMatch = rawField.match(/^\[(?:CTX|INP)\]\s*(.*)/i);
    if (bracketMatch) return bracketMatch[1].trim();

    // Handle underscore format: "CTX_name"
    const underscoreMatch = rawField.match(/^(?:CTX|INP)_(.*)/i);
    if (underscoreMatch) return underscoreMatch[1].trim();

    return rawField.trim();
  }

  /**
   * Normalizes a parameter ID for robust comparison.
   */
  private normalizeParamId(id: string): string {
    if (!id) return '';
    return id
      .replace(/^\[(?:CTX|INP)\]/i, '') // Remove [CTX] or [INP]
      .replace(/^(?:CTX|INP)_/i, '')    // Remove CTX_ or INP_
      .replace(/[\s_-]/g, '')           // Remove spaces, underscores, and dashes
      .toLowerCase();                   // To lowercase
  }

  /**
   * Helper to extract actual text values from HTML strings for a specific param ID
   */
  private extractValuesForParam(test: TestWithMetadata, paramId: string): string[] {
    const values: string[] = [];
    // Regex matches: <span ... data-field="TargetParam" ...>VALUE</span>
    const regex = new RegExp(`<span[^>]*data-field="${paramId}"[^>]*>(.*?)</span>`, 'gi');

    Object.values(test.data).forEach(val => {
      if (typeof val === 'string') {
        let match;
        while ((match = regex.exec(val)) !== null) {
          values.push(match[1]); // match[1] is the inner text
        }
      }
    });
    return values;
  }

  /**
   * THE LOGIC TO APPLY CHANGES
   */
  saveParameterChange() {
    if (!this.selectedParamState || !this.editValue) return;

    const paramId = this.selectedParamState.paramId;
    const oldVal = this.selectedParamState.currentValue;
    const newVal = this.editValue;
    const scope = this.editScope;

    let updateCount = 0;
    let testsToUpdate: TestWithMetadata[] = [];

    // Determine which tests to process
    if (scope === 'single' && this.selectedTest) {
      testsToUpdate = [this.selectedTest];
    } else {
      testsToUpdate = this.selectedParamState.testsAffected;
    }

    testsToUpdate.forEach(test => {
      let testUpdated = false;
      const newData = { ...test.data };

      Object.keys(newData).forEach(key => {
        const cellValue = newData[key];
        if (typeof cellValue !== 'string') return;

        let newCellValue = cellValue;

        if (scope === 'all_occurrences') {
          // Replace ANY occurrence of this param ID
          const regex = new RegExp(`(<span[^>]*data-field="${paramId}"[^>]*>)(.*?)(</span>)`, 'gi');
          const hasMatch = newCellValue.includes(`data-field="${paramId}"`);
          if (hasMatch) {
            newCellValue = newCellValue.replace(regex, `$1${newVal}$3`);
            testUpdated = true;
          }
        } else {
          // Replace only specific value matches (Exact match on inner text)
          const escapedOldVal = oldVal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(<span[^>]*data-field="${paramId}"[^>]*>)(${escapedOldVal})(</span>)`, 'g');

          const hasMatch = newCellValue.includes(`data-field="${paramId}"`);
          if (hasMatch) {
            newCellValue = newCellValue.replace(regex, `$1${newVal}$3`);
            testUpdated = true;
          }
        }

        if (newCellValue !== cellValue) {
          newData[key] = newCellValue;
        }
      });

      if (testUpdated) {
        test.data = newData;
        updateCount++;
        // Emit update event to parent
        this.testUpdated.emit(test);
      }
    });

    this.toastr.success(`Updated ${updateCount} test(s) with new value: "${newVal}"`);
    this.modalService.dismissAll();
  }

  /**
   * 3. Cleans Rules String.
   * Removes "TYPE: XXX." and returns the rest.
   */
  getCleanRule(rule: string): string {
    if (!rule) return '';
    // Match "TYPE: word" followed by optional dot or punctuation and space
    // Replace with empty string to leave only the purpose
    return rule.replace(/^TYPE:\s*[A-Za-z]+[\.,;]?\s*/i, '').trim();
  }

  // Get references to template
  @ViewChild('parameterModal') parameterModalTemplate: any;
}
